import { Store, CreateStorePayload, UpdateStorePayload } from '../types/store';
import { ApiErrorResponse } from '../types/auth';
import { PaginationParams, PaginatedResponse, buildQueryString } from '../types/pagination';
import { STORE_ENDPOINTS } from '../utils/endPoints';
import { createApiClient } from '../utils/axiosConfig';
import axios from 'axios';

const apiClient = createApiClient();

export const getUserStores = async (params?: PaginationParams): Promise<PaginatedResponse<Store>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ stores: Store[]; total: number; page: number; limit: number; totalPages: number }>(
      `${STORE_ENDPOINTS.MY_STORES}${qs}`
    );
    const d = response.data;
    return {
      items: d.stores ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch stores');
    } else {
      console.error('Get user stores service error:', error);
      throw new Error('An unexpected error occurred while fetching your stores.');
    }
  }
};

export const getAllStores = async (params?: PaginationParams): Promise<PaginatedResponse<Store>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ stores: Store[]; total: number; page: number; limit: number; totalPages: number }>(
      `${STORE_ENDPOINTS.ALL_STORES}${qs}`
    );
    const d = response.data;
    return {
      items: d.stores ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch all stores');
    } else {
      console.error('Get all stores service error:', error);
      throw new Error('An unexpected error occurred while fetching all stores.');
    }
  }
};

export const createNewStore = async (storeData: CreateStorePayload): Promise<{ store: Store, message: string }> => {
  try {
    const response = await apiClient.post<{ store: Store, message: string }>(STORE_ENDPOINTS.CREATE, storeData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to create store');
    } else {
      console.error('Create store service error:', error);
      throw new Error('An unexpected error occurred while creating the store.');
    }
  }
};

export const updateExistingStore = async (storeId: number, storeData: UpdateStorePayload): Promise<{ store: Store, message: string }> => {
  try {
    const response = await apiClient.put<{ store: Store, message: string }>(STORE_ENDPOINTS.UPDATE(storeId), storeData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to update store');
    } else {
      console.error('Update store service error:', error);
      throw new Error('An unexpected error occurred while updating the store.');
    }
  }
};

export const getPendingStoreRequests = async (params?: PaginationParams): Promise<PaginatedResponse<Store>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ stores: Store[]; total: number; page: number; limit: number; totalPages: number }>(
      `${STORE_ENDPOINTS.PENDING_REQUESTS}${qs}`
    );
    const d = response.data;
    return {
      items: d.stores ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch pending store requests');
    }
    console.error('Get pending store requests error:', error);
    throw new Error('An unexpected error occurred while fetching pending store requests.');
  }
};

export const acceptStoreRequest = async (storeId: number): Promise<{ store: Store, message: string }> => {
  try {
    const response = await apiClient.put<{ store: Store, message: string }>(STORE_ENDPOINTS.ACCEPT(storeId));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to accept store request');
    }
    console.error('Accept store request error:', error);
    throw new Error('An unexpected error occurred while accepting store request.');
  }
};

export const rejectStoreRequest = async (storeId: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(STORE_ENDPOINTS.REJECT(storeId));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to reject store request');
    }
    console.error('Reject store request error:', error);
    throw new Error('An unexpected error occurred while rejecting store request.');
  }
};

export const deleteStore = async (storeId: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(STORE_ENDPOINTS.DELETE(storeId));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to delete store');
    }
    console.error('Delete store error:', error);
    throw new Error('An unexpected error occurred while deleting store.');
  }
}; 